import { HotelInfo } from "@website/accommodation-ui/hotel-card-new";
import { fetcher } from "@website/fetcher/fetcher";
import { getChannel } from "@website/utils/channel/get-channel";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isChannel } from "@website/utils/channel/helpers";
import {
  dateFormat,
  daysBetweenDates,
  today,
  tomorrow
} from "@website/utils/date-format";
import { isSSR } from "@website/utils/is-ssr";
import { numberConvert } from "@website/utils/number-convert";
import { replaceOptions } from "@website/utils/replace-options";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import get from "lodash-es/get";
import inRange from "lodash-es/inRange";
import isEmpty from "lodash-es/isEmpty";
import omit from "lodash-es/omit";
import omitBy from "lodash-es/omitBy";
import pick from "lodash-es/pick";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import type from "markdown";
import { GetServerSideProps } from "next";
import { filterXSS } from "xss";
import { ENDPOINTS } from "../../constants";
import {
  HotelDomesticRateDataType,
  HotelDomesticReviewDataType,
  PersianDomesticRatings
} from "./[hotelTitle]/index.types";
import {
  CityPagePropertiesType,
  HotelCategoryBadgeType,
  HotelCityFacilities,
  HotelDomesticSearchTextType,
  HotelDomesticSuggestedType,
  HotelDomesticType
} from "./index.types";

export const transformHotelSchema = (
  data: HotelDomesticType | HotelDomesticSuggestedType,
  query: unknown,
  dataType: "HotelDomestic" | "HotelDomesticSuggested",
  earlyBirdBadges?: Array<{ date: string; hotelId: number }>,
  lastSecondHotels?: Array<HotelInfo>
) => {
  const advertisementBadge = data?.badges?.find(
    ({ type }) => type === "advertisement" && data.is_available
  ) as HotelDomesticType["badges"][0];
  const partnershipBadge = data?.badges?.find(
    ({ type }) => type === "partnership" && data.is_available
  ) as HotelDomesticType["badges"][0];
  const ordinaryBadges = data?.badges?.filter(
    ({ type }) => type === "ordinary"
  ) as HotelDomesticType["badges"];

  return {
    id: data.id,
    isInstant: data?.instant_book || true,
    ruleId: data.rule_id || null,
    thumbnail: {
      alt: data.cover_media.title,
      url: toString(data.cover_media.media)
    },
    pricing: {
      price:
        "minimum_room_price" in data ? data.minimum_room_price : data.price,
      discountPercentage:
        "maximum_discount_percent" in data
          ? Math.ceil(data.maximum_discount_percent)
          : Math.ceil(data.discount_percent),
      priceOff:
        "minimum_room_price_off" in data
          ? data.minimum_room_price_off
          : data.price_off,
      hasSnappProDiscount: data.snapp_pro_discount || false
    },
    primaryBadge: {
      icon: "celebration" as const,
      color: "white",
      text: toString(partnershipBadge?.title),
      description: toString(partnershipBadge?.description),
      transparent: false
    },
    regularBadges: [
      ...(earlyBirdBadges?.find((badge) => badge?.hotelId === data?.id)
        ?.hotelId &&
      data?.pe_badge_title !== "early_bird" &&
      data?.price - data?.price_off === 0
        ? [
            {
              color: "#555eff1a",
              textStyle: "text-secondary",
              icon: "event",
              tooltip: `در صورت تغییر تاریخ ورود به ${dateFormat(
                earlyBirdBadges?.find((badge) => badge?.hotelId === data?.id)
                  ?.date,
                { locale: "fa", template: "dd MMM " }
              )} تخفیف زودهنگام برای شما فعال میشود`,
              text: `تخفیف زود هنگام (برای ورود از  ${dateFormat(
                earlyBirdBadges?.find((badge) => badge?.hotelId === data?.id)
                  ?.date,
                { locale: "fa", template: "dd MMM" }
              )})`
            }
          ]
        : []),
      ...(lastSecondHotels?.find((hotel) => hotel?.id === data?.id)?.id &&
      data?.pe_badge_title !== "last_minute" &&
      data?.price - data?.price_off === 0
        ? [
            {
              color: "#ff00001a",
              textStyle: "text-primary",
              icon: "timer",
              tooltip:
                "در صورت تغییر تاریخ ورود به امشب یا فردا شب تخفیف لحظه آخری فعال خواهد شد",
              text: `تخفیف لحظه آخری برای امشب`
            }
          ]
        : []),
      ...("pe_badge_title" in data && data.pe_badge_title?.length
        ? [
            {
              color: {
                last_minute: "#ED071A",
                early_bird: "#575eff"
              }[data.pe_badge_title],
              text: {
                last_minute: "تخفیف لحظه آخری",
                early_bird: "تخفیف خرید زود هنگام"
              }[data.pe_badge_title],
              icon: {
                last_minute: "timer" as const,
                early_bird: "event" as const
              }[data.pe_badge_title],
              transparent: false
            }
          ]
        : []),
      ...(ordinaryBadges?.splice(0, 3)?.map(({ color, title }) => ({
        color,
        text: title,
        transparent: true
      })) ?? [])
    ],
    advertisementBadge: {
      color: toString(advertisementBadge?.color),
      text: toString(advertisementBadge?.title),
      description: toString(advertisementBadge?.description),
      transparent: false
    },
    hotelDetail: {
      cityId: data.city_id,
      hotelId: data.id,
      address: isChannel(["telesale"])
        ? data.address
        : data.address.split("،").slice(0, 2).join("،"),
      isAvailable: data.is_available,
      cityTitle: data.city_title,
      accommodationType: data.accommodation_type,
      hotelTitle: `${
        dataType === "HotelDomesticSuggested"
          ? data.accommodation_title
          : data.accommodation_type
      } ${data.title} ${data.city_title}`,
      stars: data.stars,
      rating: toNumber(data.reviews.ptp_ratings),
      reviewCount: toNumber(data.reviews.ptp_reviews),
      roomTitle: data.min_price_room_title || "",
      roomCount: data.min_price_available_room_count || 0,
      hotelPageUrl: {
        pathname: "/[cityTitle]/[hotelTitle]",
        query: {
          cityTitle: data.city_url,
          hotelTitle: data.url
        }
      },
      isFomo: inRange(data.min_price_available_room_count ?? 0, 1, 3)
    },
    hotelOffers: {
      offers: data.categories?.map?.(({ name }) => name) ?? []
    },
    reservationInfo: {
      bedCount: 1,
      nightCount:
        daysBetweenDates(get(query, "date_from"), get(query, "date_to")) || 1
    },
    facilities:
      data?.facilities
        ?.map((tag) => ({
          ...tag,
          icon: tag.icon === "what.ico" ? "check_box" : tag.icon
        }))
        .slice(0, 5) ?? [],
    location: {
      lat: data?.location?.[0] || 0,
      lng: data?.location?.[1] || 0
    },
    gallery: [
      ...(data?.galleries
        ?.map((item) => ({
          id: item?.id,
          title: item?.title,
          media: item?.media,
          order: item?.order_no
        }))
        .sort((a, b) => a.order - b.order) || [])
    ]
  };
};
export const fetchSeoData = (
  resolvedUrl: string,
  fallbackTitle: string,
  description: string = ""
) => {
  const newResolvedUrl = decodeURI(
    `${resolvedUrl?.replaceAll(/(\?.*)|(#.*)/g, "")}`
  );
  return fetcher({
    url: ENDPOINTS.SEO_CONTENT,
    method: "POST",
    data: {
      path: newResolvedUrl
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.SEO_CONTENT, resolvedUrl]
    }
  })
    .then(({ data }) => ({
      ...data,
      robots: data?.robots || "",
      redirect: data?.redirect || "",
      seoContents:
        data?.seoContents?.map((content) => ({
          ...content,
          description: filterXSS(content?.description, {
            css: false,
            whiteList: { a: ["href", "title", "target"], br: [] },
            stripIgnoreTag: true,
            stripIgnoreTagBody: ["script"]
          }).replaceAll("<br>", "</br>")
        })) ?? [],
      meta: {
        ...data?.meta,
        fallbackTitle: replaceSnappTripBasedOnChannel(fallbackTitle),
        description: data?.meta?.description || description,
        resolvedUrl: newResolvedUrl
      }
    }))
    .catch(() => ({
      robots: "",
      redirect: "",
      faq: [],
      id: 0,
      inboundLinks: [],
      meta: {
        canonical: resolvedUrl,
        description,
        metaTag: [],
        title: fallbackTitle,
        fallbackTitle: fallbackTitle,
        resolvedUrl
      },
      pageTitle: fallbackTitle,
      path: resolvedUrl,
      seoContents: []
    }));
};

export const fetchCategoriesData = (cityId: unknown) =>
  fetcher({
    url: ENDPOINTS.CITY_CATEGORIES_GET,
    method: "POST",
    params: { token: "Jek" },
    data: { cityIds: [cityId] },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.CITY_CATEGORIES_GET, toString(cityId)]
    }
  })
    .then(
      ({ data }) =>
        data?.data?.[0]?.categories?.map?.(({ id, name }) => ({
          label: name,
          name: toString(id)
        })) || []
    )
    .catch(() => []);

export const fetchBannersData = (page: string, type: string, query: unknown) =>
  fetcher({
    url: ENDPOINTS.GET_CITY_PAGE_BANNERS,
    method: "POST",
    data: {
      extraData: {
        cityTitle: get(query, "cityTitle")
      },
      channel: getChannel(),
      product: "hotel-domestic",
      page,
      type
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.GET_BANNERS, page, type, get(query, "cityTitle")]
    }
  })
    .then(({ data }) => data)
    .catch(() => []);

export const fetchHotelSuggestionsData = (query?: unknown) =>
  fetcher({
    url: ENDPOINTS.CITY_SUGGESTION_GET,
    options: { cityTitle: get(query, "cityTitle") },
    method: "GET",
    params: {
      token: "Jek"
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.CITY_SUGGESTION_GET, toString(get(query, "cityTitle"))]
    }
  })
    .then(
      ({ data }) =>
        data?.map(
          (item) => transformHotelSchema(item, query, "HotelDomesticSuggested"),
          query
        ) || []
    )
    .catch(() => []);

export const fetchHotelsData = (
  cityId: unknown,
  _query?: unknown,
  stars?: unknown,
  recommendation?: boolean
) => {
  const query = isSSR() ? { city_id: cityId } : _query;
  const parameters = omitBy(
    {
      city_id: cityId,
      date_from: get(query, "date_from") || today(),
      date_to: get(query, "date_to") || tomorrow(),
      page: get(query, "page") || 1,
      order_by: get(query, "order_by") || "selling",
      accommodations: get(query, "accommodations"),
      promotion_types: "",
      categories: get(query, "categories"),
      category_ids: get(query, "category_ids"),
      stars: stars || toString(get(query, "stars")).replaceAll("-", ","),
      facilities: toString(get(query, "facilities")).replaceAll("-", ","),
      hotel_name: get(query, "hotel_name"),
      rate: get(query, "rate") === "all" ? "" : get(query, "rate"),
      price_from: get(query, "price_from"),
      price_to: get(query, "price_to"),
      availability: get(query, "availability"),
      token: "Jek",
      no_rooms: get(query, "no_rooms") || 1,
      discounted: get(query, "discounted")
        ? get(query, "discounted") !== "false"
        : false,
      recommendation
    },
    (value) => !value
  );
  return fetcher({
    url: ENDPOINTS.CITY_SEARCH,
    params: parameters,
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.CITY_SEARCH, JSON.stringify(parameters)]
    }
  })
    .then(({ data }) => {
      const currentDate = new Date().toISOString().split("T")?.[0];
      const diff = Math.ceil(
        Math.abs(
          new Date(query?.["date_from"] ?? currentDate).getTime() -
            new Date(currentDate).getTime()
        ) /
          (1000 * 60 * 60 * 24)
      );

      if (diff === 6)
        return fetchEarlyBirdBadges(query).then(
          (earlyBirdBadges) =>
            (data?.data?.map((item) => ({
              ...transformHotelSchema(
                item,
                query,
                "HotelDomestic",
                earlyBirdBadges
              ),
              total: toNumber(data?.total || 0),
              page: toNumber(get(query, "page") || 1)
            })) ?? []) as CityPagePropertiesType["hotelsData"]
        );
      if (diff >= 2 && diff <= 5)
        return fetchLastSecondHotels(query).then(
          (lastSecondHotels) =>
            (data?.data?.map((item) => ({
              ...transformHotelSchema(
                item,
                query,
                "HotelDomestic",
                [],
                lastSecondHotels
              ),
              total: toNumber(data?.total || 0),
              page: toNumber(get(query, "page") || 1)
            })) ?? []) as CityPagePropertiesType["hotelsData"]
        );
      return (data?.data?.map((item) => ({
        ...transformHotelSchema(item, query, "HotelDomestic"),
        total: toNumber(data?.total || 0),
        page: toNumber(get(query, "page") || 1)
      })) ?? []) as CityPagePropertiesType["hotelsData"];
    })
    .catch(() => []);
};

export const fetchCityData = (parameters: unknown) =>
  fetcher({
    url: ENDPOINTS.CITY_GET,
    options: {
      cityTitle: get(parameters, "cityTitle")
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.CITY_GET, toString(get(parameters, "cityTitle"))]
    }
  }).then(({ data }) => ({
    id: data.id,
    url: data.fa_url,
    title: data.fa_title,
    ...data
  }));

export const fetchGeoData = (id: unknown, geoType: string) =>
  fetcher({
    url: ENDPOINTS.GEO_FIND,
    params: { geoType, id },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.GEO_FIND]
    }
  }).then(({ data }) => ({
    id: data.id,
    slug: data?.slug,
    title: data?.title,
    type: data?.type
  }));

export const fetchCityFilters = (cityId: number, query?: unknown) =>
  cityId
    ? fetcher({
        url: ENDPOINTS.CITY_FILTERS,
        options: { cityId },
        params: {
          onlyAvailable: true,
          ...(query?.["date_from"] && { date_from: query?.["date_from"] }),
          ...(query?.["date_to"] && { date_to: query?.["date_to"] })
        },
        next: {
          revalidate: 300,
          tags: [ENDPOINTS.CITY_FILTERS, toString(cityId)]
        }
      })
        .then(({ data }) => ({
          accommodations:
            data?.accommodations?.map((item) => ({
              label: item.title,
              type: item.type
            })) || [],
          priceRange: {
            min: data?.pricing?.min ?? get(query, "price_from") ?? 100_000,
            max: data?.pricing?.max ?? get(query, "price_to") ?? 1_550_000
          },
          facilities: (data?.facilities || [])?.map((facility) => ({
            id: facility?.id,
            title: facility?.title,
            icon: facility?.icon
          })),
          categories: (data?.categories || [])?.map((category) => ({
            id: category?.id,
            name: category?.name
          })),
          rates: data?.rates?.reverse() || [],
          stars: data?.stars || []
        }))
        .catch(() => ({
          accommodations: [],
          priceRange: { min: 100_000, max: 15_500_000 },
          facilities: [],
          categories: [],
          stars: [],
          rates: []
        }))
    : new Promise<{
        accommodations: Array<{ label: string; type: string }>;
        stars: Array<number>;
        rates: Array<number>;
        priceRange: { min: number; max: number };
        facilities: Array<HotelCityFacilities>;
        categories: Array<HotelCategoryBadgeType>;
      }>((resolve) =>
        resolve({
          accommodations: [],
          priceRange: { min: 100_000, max: 15_500_000 },
          facilities: [],
          categories: [],
          stars: [],
          rates: []
        })
      );

export const fetchSearchTextData = (text: string, locale?: string) =>
  fetcher({
    url: ENDPOINTS.CITY_AUTOCOMPLETE,
    params: {
      text,
      token: "Jek",
      provider: "flightio"
    }
  }).then(
    ({ data }) =>
      ((data?.data ?? []) as HotelDomesticSearchTextType)?.map((item) => ({
        id: toString(item.id),
        title: item.fa_title,
        url: item.fa_url,
        hotel_count: item.hotel_count,
        info: item.fa_url.includes("/")
          ? item.stars
            ? `${numberConvert(item.stars, { locale })} ستاره`
            : ""
          : item.hotel_count
            ? `${numberConvert(item.hotel_count, { locale })} اقامتگاه`
            : "",
        stars: item.stars,
        isHotel: item.fa_url.includes("/"),
        subtitle: item.fa_url.includes("/")
          ? item.fa_city_title || item.fa_state_title
          : item.fa_state_title || item.fa_city_title,
        lat: item?.location?.lat,
        long: item?.location?.lon
      }))
  );

export const fetchLastSecondHotels = async (_query: unknown) => {
  const query = isSSR()
    ? pick(_query, ["cityTitle", "date_from", "date_to", "order_by"])
    : _query;
  if ([tomorrow(), today()].includes(get(query, "date_from"))) {
    const parameters = {
      date_from: get(query, "date_from"),
      date_to: get(query, "date_to"),
      page: get(query, "page") || 1,
      order_by: get(query, "order_by") || "selling",
      accommodations: get(query, "accommodations"),
      promotion_types: "",
      categories: get(query, "categories"),
      stars: toString(get(query, "stars")).replaceAll("-", ","),
      facilities: toString(get(query, "facilities")).replaceAll("-", ","),
      hotel_name: get(query, "hotel_name"),
      rate: get(query, "rate") === "all" ? "" : get(query, "rate"),
      price_from: get(query, "price_from"),
      price_to: get(query, "price_to"),
      availability: get(query, "availability")
    };

    return fetcher({
      url: ENDPOINTS.GET_LAST_SECOND_HOTELS,
      options: {
        cityTitle: get(query, "cityTitle")
      },
      params: parameters,
      next: {
        revalidate: 300,
        tags: [ENDPOINTS.GET_LAST_SECOND_HOTELS, JSON.stringify(query)]
      }
    })
      .then(
        ({ data }) =>
          (data?.data?.map((item) => ({
            ...transformHotelSchema(item, query, "HotelDomestic"),
            total: toNumber(data.total || 0),
            page: toNumber(get(query, "page") || 1)
          })) ?? []) as CityPagePropertiesType["hotelsData"]
      )
      .catch(() => []);
  }

  return new Promise<CityPagePropertiesType["hotelsData"]>((resolve) =>
    resolve([])
  );
};

export const fetchInstalmentWallet = (): Promise<{
  isEligible: boolean;
  showDownPayment: boolean;
  markup: number;
}> =>
  fetcher({
    url: ENDPOINTS.INSTALMENT_WALLET,
    method: "GET",
    headers: {
      product: "HOTEL"
    }
  }).then(({ data }) => ({
    isEligible: data?.is_eligible,
    showDownPayment: data?.show_down_payment,
    markup: data?.markup
  }));

export const fetchEarlyBirdBadges = (query: unknown) =>
  fetcher({
    url: ENDPOINTS.GET_EARLY_BIRDS_BADGES,
    method: "GET",
    options: {
      cityTitle: get(query, "cityTitle")
    }
  })
    .then(
      ({ data }) =>
        data?.map((item) => ({
          date: item?.date,
          hotelId: item?.hotel_id
        })) ?? []
    )
    .catch(() => []);

export const fetchHotelRateData = (hotelId: unknown) =>
  fetcher({
    url: ENDPOINTS.HOTEL_RATE,
    options: { hotelId },
    method: "GET",
    headers: {
      product: "hotel-domestic"
    },
    next: {
      tags: [ENDPOINTS.HOTEL_RATE, toString(hotelId)]
    }
  }).then(({ data }) => {
    const _data = data as HotelDomesticRateDataType;
    return {
      rateDetail: {
        clean_rate: _data?.cleaning || "",
        facility_rate: _data?.facilities || "",
        food_quality_rate: _data?.food || "",
        location_rate: _data?.location || "",
        staff_rate: _data?.friendly || "",
        value_for_money_rate: _data?.priceQuality || ""
      },
      rate: Number(_data?.overall).toFixed(1) ?? "",
      rateCount: _data?.rateCount ?? 0,
      offerCount: _data?.recommendationCount ?? 0,
      offerMessage:
        _data?.recommendationType === "recommendation"
          ? "۷۵ درصد از میهمانان اخیر، این اقامتگاه رو پیشنهاد کرده اند. "
          : _data?.recommendationType === "fullRate"
            ? "میهمانان در ۵ رزرو اخیر، امتیاز کامل را به اقامتگاه داده اند"
            : _data?.recommendationType
              ? `میهمانان در ۵ رزرو اخیر، به  "${PersianDomesticRatings?.[
                  _data?.recommendationType
                ]}" امتیاز کامل داده اند.`
              : "",
      aiSummary: type?.markdown?.toHTML(data?.aiSummary)
    };
  });
export const fetchHotelReviewData = (
  hotelId: unknown,
  page: unknown,
  product: string,
  filters: Record<string, string>,
  roomId?: unknown
) =>
  fetcher({
    url: replaceOptions(ENDPOINTS.HOTEL_REVIEW, { hotelId }),
    method: "GET",
    headers: {
      product
    },
    params: {
      ...filters,
      roomId: roomId || "",
      pageSize: 5,
      page
    }
  }).then(({ data }) => {
    const _data = data as HotelDomesticReviewDataType;
    return {
      page: toNumber(_data?.page),
      total: _data?.total,
      rateReviews: (_data?.result ?? [])?.map((comment) => ({
        id: comment?.id,
        helpfulSubmitted: comment?.helpfulSubmitted,
        userName: comment?.passenger,
        roomName: comment.roomTitle,
        rate: String(comment?.avgRating || 0),
        registeredDate: comment?.createdAt,
        standNights: comment?.dateCount || 1,
        roomId: comment?.roomId,
        createdAt: comment?.createdAt,
        comment:
          replaceSnappTripBasedOnChannel(
            filterXSS(comment?.comment, {
              whiteList: {},
              stripIgnoreTag: true,
              stripIgnoreTagBody: ["script"]
            })
          ) ?? "",
        strengths: comment?.pros?.filter(Boolean)?.join(" ,") ?? "",
        weakness: comment?.cons?.filter(Boolean)?.join(" ,") ?? "",
        source: comment?.source ?? "",
        pictures: comment?.images?.map((image, index) => ({
          id: index,
          media: `${process.env["NEXT_PUBLIC_BASE_URL_STORE"]}/reviews/${image}`,
          title: ""
        }))
      }))
    };
  });

export const fetchLocationData = (cityId: string) =>
  fetcher({
    url: ENDPOINTS.GEO_CITY_ID,
    options: {
      id: cityId
    },
    next: {
      revalidate: 300,
      tags: [ENDPOINTS.GEO_CITY_ID, cityId]
    }
  })
    .then(({ data }) => ({
      id: data?.id ?? "",
      url: data?.url ?? "",
      title: data?.title ?? "",
      location: data?.location,
      ...data
    }))
    .catch(() => ({
      id: -1,
      url: "",
      title: "",
      location: null
    }));
export const getServerSideProps: GetServerSideProps<
  CityPagePropertiesType
> = async ({ query, res }) => {
  const cityData = await fetchCityData(query);
  if (!cityData?.id || toString(query.cityTitle).startsWith("هتل")) {
    res.statusCode = 410;
    return { props: {} };
  }

  const [
    locationData,
    seoData,
    hotelsData,
    hotelSuggestionsData,
    lastSecondHotels,
    inlineBanners,
    staticBanners,
    smallBanners
  ] = await Promise.all([
    fetchLocationData(cityData?.id || ""),
    isChannel(["website", "web", "snapp-ir"])
      ? fetchSeoData(
          `${getOrigin()}${
            process.env.NEXT_PUBLIC_BASE_PATHNAME
          }/${query?.cityTitle}`,
          cityData?.title
        )
      : Promise.resolve(null),
    isEmpty(omit(query, ["cityTitle"]))
      ? fetchHotelsData(cityData?.id, query)
      : Promise.resolve([]),
    fetchHotelSuggestionsData(query),
    fetchLastSecondHotels(query),
    fetchBannersData("Citypage2", "static", query),
    fetchBannersData("Citypage", "static", query),
    fetchBannersData("Citypage", "small", query)
  ]);

  if (seoData?.redirect) {
    return { redirect: { destination: seoData?.redirect, permanent: true } };
  }
  return {
    props: {
      seoData,
      cityData,
      hotelsData,
      hotelSuggestionsData,
      locationData,
      lastSecondHotels,
      inlineBanners,
      staticBanners,
      smallBanners
    }
  };
};
